import { Loader2 } from "lucide-react";
import { cn } from "../lib/utils";

interface LoadingStateProps {
  message?: string;
  className?: string;
  variant?: "default" | "skeleton" | "illustration";
}

export function LoadingState({
  message = "Loading...",
  className,
  variant = "illustration",
}: LoadingStateProps) {
  if (variant === "skeleton") {
    return (
      <div className={cn("space-y-4", className)}>
        {/* Skeleton cards */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="overflow-hidden rounded-2xl border bg-card">
              <div className="animate-shimmer aspect-[4/3] bg-muted" />
              <div className="space-y-3 p-4">
                <div className="animate-shimmer h-5 w-3/4 rounded bg-muted" />
                <div className="animate-shimmer h-4 w-1/2 rounded bg-muted" />
                <div className="animate-shimmer h-4 w-2/3 rounded bg-muted" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (variant === "illustration") {
    return (
      <div
        className={cn(
          "animate-fade-in flex min-h-[300px] flex-col items-center justify-center gap-4",
          className
        )}
      >
        <div className="relative h-40 w-48 sm:h-52 sm:w-64">
          <LoadingIllustration className="h-full w-full text-primary" />
        </div>
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <p className="text-sm font-medium">{message}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn("animate-fade-in flex min-h-[300px] items-center justify-center", className)}
    >
      <div className="text-center">
        <Loader2 className="mx-auto mb-4 h-10 w-10 animate-spin text-primary" />
        <p className="text-sm font-medium text-muted-foreground">{message}</p>
      </div>
    </div>
  );
}

function LoadingIllustration({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="140 100 480 430"
      className={className}
      aria-hidden="true"
    >
      {/* Shadow under person */}
      <ellipse cx="400" cy="500" rx="80" ry="15" fill="currentColor" opacity="0.1" />

      {/* Mountains background */}
      <g opacity="0.15">
        <path d="M180 380 L250 280 L320 380 Z" fill="currentColor" />
        <path d="M280 380 L380 220 L480 380 Z" fill="currentColor" />
        <path d="M420 380 L500 300 L580 380 Z" fill="currentColor" />
      </g>

      {/* Snow caps */}
      <g opacity="0.3">
        <path d="M340 260 L380 220 L420 260 Z" fill="currentColor" />
      </g>

      {/* Sun */}
      <circle cx="520" cy="160" r="25" fill="currentColor" opacity="0.15" />
      <circle cx="520" cy="160" r="18" fill="#fbbf24" opacity="0.8" />

      {/* Birds */}
      <g stroke="currentColor" strokeWidth="2" fill="none" opacity="0.4">
        <path d="M460 180 Q465 175 470 180" />
        <path d="M480 170 Q485 165 490 170" />
        <path d="M440 195 Q445 190 450 195" />
      </g>

      {/* Path/road with dashes */}
      <path
        d="M160 450 Q240 420 320 440 Q400 460 440 420 Q480 380 520 400"
        stroke="currentColor"
        strokeWidth="4"
        strokeDasharray="8 6"
        fill="none"
        opacity="0.25"
      />

      {/* Location pins */}
      <g>
        <circle cx="160" cy="450" r="10" fill="currentColor" opacity="0.4" />
        <circle cx="320" cy="440" r="8" fill="currentColor" opacity="0.35" />
        <circle cx="520" cy="400" r="8" fill="#3b82f6" opacity="0.6" />
      </g>

      {/* Person walking */}
      <g transform="translate(380, 380)">
        {/* Head */}
        <circle cx="0" cy="-80" r="22" fill="#fcd5ce" />
        {/* Hair */}
        <ellipse cx="0" cy="-90" rx="20" ry="18" fill="#5c4033" />
        {/* Body/shirt */}
        <path
          d="M-18 -55 L-18 0 Q-18 15 -5 15 L5 15 Q18 15 18 0 L18 -55 Q18 -70 0 -70 Q-18 -70 -18 -55"
          fill="currentColor"
        />
        {/* Backpack */}
        <rect x="15" y="-50" width="20" height="35" rx="4" fill="currentColor" opacity="0.7" />
        <rect x="18" y="-45" width="14" height="8" rx="2" fill="currentColor" opacity="0.5" />
        {/* Backpack straps */}
        <path d="M15 -45 L8 -55" stroke="currentColor" strokeWidth="3" opacity="0.6" />
        {/* Pants */}
        <path d="M-12 15 L-15 80 Q-15 85 -10 85 L0 85 Q5 85 5 80 L5 40" fill="#374151" />
        <path d="M12 15 L20 75 Q20 80 15 80 L5 80 Q0 80 0 75 L5 40" fill="#374151" />
        {/* Shoes */}
        <ellipse cx="-5" cy="88" rx="12" ry="5" fill="#1f2937" />
        <ellipse cx="18" cy="83" rx="12" ry="5" fill="#1f2937" />
        {/* Arms */}
        <path d="M-18 -45 L-35 -25" stroke="#fcd5ce" strokeWidth="8" strokeLinecap="round" />
        <path d="M18 -45 L40 -60" stroke="#fcd5ce" strokeWidth="8" strokeLinecap="round" />
        {/* Waving hand */}
        <circle cx="43" cy="-63" r="8" fill="#fcd5ce" />
      </g>

      {/* Ground texture */}
      <g stroke="currentColor" strokeWidth="1" opacity="0.1">
        <path d="M150 510 L550 510" />
        <path d="M180 520 L520 520" />
      </g>
    </svg>
  );
}

// Skeleton components for more specific use cases
export function SkeletonCard() {
  return (
    <div className="overflow-hidden rounded-2xl border bg-card">
      <div className="animate-shimmer aspect-[4/3] bg-muted" />
      <div className="space-y-3 p-4">
        <div className="animate-shimmer h-5 w-3/4 rounded bg-muted" />
        <div className="animate-shimmer h-4 w-1/2 rounded bg-muted" />
        <div className="animate-shimmer h-4 w-2/3 rounded bg-muted" />
      </div>
    </div>
  );
}

export function SkeletonList({ count = 3 }: { count?: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 rounded-2xl border bg-card p-4">
          <div className="animate-shimmer h-16 w-16 flex-shrink-0 rounded-xl bg-muted" />
          <div className="min-w-0 flex-1 space-y-2">
            <div className="animate-shimmer h-4 w-3/4 rounded bg-muted" />
            <div className="animate-shimmer h-3 w-1/2 rounded bg-muted" />
          </div>
        </div>
      ))}
    </div>
  );
}
